import React, { createContext, useCallback, useEffect, useReducer } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { last, uniqBy } from 'lodash';
import { useQuery } from '@apollo/client';
import { useAlert } from '@lwtears/lwt-common-frontend/lib/Alert';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import { tA11y } from '@lwtears/lwt-common-frontend/lib/@common/util';
import { capitalize } from '@mui/material';
import { defaultState, reducers } from './reducers';
import { Actions, UseGameState, UseGameDispatch, PageType, ActivityTypes } from './types';
import useAttemptStore from '../../graph/state/query-hooks/use-attempt-store';
import ActivityCompetionAlertBody from '../../components/activities/ActivityCompletionAlertMessage';
import ActivityModal from '../../components/common/Modals/ActivityModal';
import BaseModalTitle from '../../components/common/Modals/BaseModalTitle';
import NextButton from '../../components/common/Buttons/NextButton';
import activitiesQueries from '../../graph/queries/activities';
import useActivityAudio from '../../hooks/use-activity-audio';
import useActivityCompletionAlert from '../../hooks/use-activity-completion-alert';
import usePage from '../../hooks/use-page';
import { SoundType } from '../ActivityAudioProvider/types';
import ActivityTransformers from '../../transformers';
import genericSounds from '../../transformers/genericSounds';
import { AttemptStatus, BookType } from '../../graph/schema-generated-types-educator';
import { NavigationDirection, AppRoutes, ButtonSize } from '../../types';
import isLastPage from '../../util/is-last-page-util';
import { getActivitySelectionModalRoute } from '../../util/navigation-util';
import { getTotalPages } from '../../util/paging-util';

export type UseGame<T> = [UseGameState<T>, UseGameDispatch];

export const GameContext = createContext<any>([]);

type GameProviderProps = {
  character: string;
  activityType: string;
  isStudentApp?: boolean;
};

export const GameProvider: React.FC<GameProviderProps> = (props) => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [, { handleSaveAttempt }] = useAttemptStore();

  const assignmentActivityId = params?.assignmentActivityId;

  const [
    { currentPage, noOfPages, totalPages },
    {
      setNumberOfTotalPages,
      resetCurrentPageNumber,
      handlePageTurn,
      setFirstPage,
      getCurrentPageData,
    },
  ] = usePage();
  const { character, activityType, isStudentApp = false } = props;

  const [, { openModal }] = useModal();
  const [, { closeAlert }] = useAlert();

  const [state, dispatch] = useReducer<React.Reducer<UseGameState<any>, Actions>>(
    reducers,
    defaultState()
  );
  const [audioState, { loadAudioAssets }] = useActivityAudio();

  const queryName = `${activityType}${isStudentApp ? 'Student' : ''}`;
  const activityQuery = activitiesQueries[queryName];

  const { data, error, loading } = useQuery(activityQuery, {
    fetchPolicy: activityType === ActivityTypes.FIND_THE_OBJECTS ? 'no-cache' : 'cache-first',
    variables: {
      where: {
        book: {
          character: {
            character: character,
          },
          edition: 2022,
          type: BookType.LETTER_BOOK,
        },
      },
    },
  });

  if (error) dispatch({ type: 'HANDLE_ERROR', payload: { error } });

  useEffect(() => {
    if (loading) return;

    const activityData = data[Object.keys(data)[0]];
    dispatch({ type: 'SET_DATA', payload: { ...activityData } });

    const firstPage = activityData?.pages[0].page.pageNumber;
    const lastPage = last(activityData?.pages as any[]).page.pageNumber;

    setFirstPage(firstPage);
    setNumberOfTotalPages(getTotalPages(firstPage, lastPage));
  }, [loading]);

  useEffect(() => {
    if (loading || state.SVGsLoading || audioState.isLoading) return;

    dispatch({ type: 'HANDLE_LOADING', payload: { loading: false } });
  }, [loading, state.SVGsLoading, audioState.isLoading]);

  useEffect(() => {
    const {
      activityData,
      activityData: { pages, sound },
    } = state;

    // TODO: define a generic list which will be loaded for each activity
    const effects = [genericSounds.BOINK];

    if (!activityData || !pages) return;

    const loader: (data: PageType) => SoundType[] = ActivityTransformers[activityType];

    const assetsToLoad: SoundType[] = [];

    pages.map((page: PageType) => assetsToLoad.push(...loader(page)));

    sound && assetsToLoad.push(...loader(sound));
    assetsToLoad.push(...effects);

    loadAudioAssets(uniqBy(assetsToLoad, 'name'));

    if (activityType === ActivityTypes.FIND_THE_OBJECTS) preloadSVGs(pages);
    else dispatch({ type: 'HANDLE_SVG_LOADING', payload: { loading: false } });
  }, [state.activityData, totalPages]);

  const loadSVG = async (svgSrc: string): Promise<any> =>
    new Promise((resolve) => {
      axios
        .get(svgSrc)
        .then((data) => resolve(data))
        .catch((err) => {
          console.warn(err);
          resolve(err);
        });
    });

  const preloadSVGs = async (pages) => {
    dispatch({ type: 'HANDLE_SVG_LOADING', payload: { loading: true } });

    const SVGsToPreload = pages.filter((page) => page.svg).map((page) => page.svg.src);
    const fistElement = SVGsToPreload.shift();

    //load the first svg
    await loadSVG(fistElement);
    dispatch({ type: 'HANDLE_SVG_LOADING', payload: { loading: false } });

    //load the rest behind the scenes
    SVGsToPreload.map((src) => loadSVG(src));
  };

  const triggerActivityModal = (
    scope: string,
    onContinueActions: () => void,
    direction?: NavigationDirection,
    onCloseActions?: () => void,
    handleAfterContinue?: () => void
  ) => {
    const scopeTranslations = state[scope];
    openModal({
      Title: () => (
        <BaseModalTitle
          text={tA11y(`activitiesModal.${scopeTranslations?.title}`, {
            direction: capitalize(
              direction === NavigationDirection.Next
                ? tA11y('activitiesModal.next')
                : tA11y('activitiesModal.previous')
            ),
          })}
        />
      ),
      body: (
        <ActivityModal
          {...scopeTranslations}
          direction={direction}
          onContinue={() => {
            onContinueActions?.();
          }}
          onClose={() => {
            onCloseActions?.();
          }}
          handleAfterContinue={handleAfterContinue}
        />
      ),
    });
  };

  const handleTurnActivityPage = (direction: NavigationDirection) => {
    if (state.activityState.currentPageCompleted) {
      handlePageTurn(direction);
    } else {
      triggerActivityModal('unfinishedActivity', () => handlePageTurn(direction), direction);
    }
  };

  const redirectToActivitySelectionPage = () => navigate(getActivitySelectionModalRoute(character));

  const handleGameEnded = (redirect?: boolean) => {
    closeAlert();
    onGameEnd();
    redirect && redirectToActivitySelectionPage();
  };

  const handleCloseActivity = useCallback(() => {
    const lastPageCompleted = state.activityState.currentPageCompleted && isLastPage(state);
    lastPageCompleted
      ? handleGameEnded(true)
      : triggerActivityModal('closeActivity', handleGameEnded, undefined, undefined, () =>
          setTimeout(() => redirectToActivitySelectionPage())
        );
  }, [state.activityState.currentPageCompleted]);

  const onGameEnd = useCallback(() => {
    dispatch({ type: 'ON_GAME_END', payload: { gameEnd: true } });
  }, [dispatch, state]);

  const resetActivity = useCallback(
    (isRestarting: boolean) => {
      dispatch({ type: 'RESET_ACTIVITY', payload: { isRestarting } });
    },
    [dispatch, state]
  );

  const setCurrentData = useCallback(
    (currentData: any[]) => {
      dispatch({ type: 'SET_CURRENT_DATA', payload: { currentData } });
    },
    [dispatch, state]
  );

  const setIsActivityOver = (isActivityOver: boolean) => {
    dispatch({
      type: 'SET_IS_ACTIVITY_OVER',
      payload: isActivityOver,
    });
  };

  const handleRestartActivity = useCallback(() => {
    resetActivity(true);
    resetCurrentPageNumber();
    setTimeout(() => resetActivity(false), 1000);
  }, []);

  const { handleActivityCompletionAlert } = useActivityCompletionAlert(
    handleRestartActivity,
    () => handleGameEnded(true),
    <ActivityCompetionAlertBody
      activityType={activityType}
      letter={character}
      bookTitle={state.activityData.book?.title || ''}
    />,
    null
  );

  const handleCurrentPageCompletion = useCallback(
    (completed: boolean) => {
      dispatch({
        type: 'HANDLE_CURRENT_PAGE_COMPLETION',
        payload: { completed },
      });
    },
    [dispatch, state]
  );

  const handleActivityCompletion = () => {
    if (!assignmentActivityId) return;
    navigate({
      pathname: `/${AppRoutes.ACTIVITY_COMPLETE}/${assignmentActivityId}`,
      search: location.search,
    });
  };

  const handleCurrentPageCompletionWithAlert = useCallback(
    (completed: boolean) => {
      handleCurrentPageCompletion(completed);
      if (completed && isLastPage(state)) {
        if (!assignmentActivityId) {
          handleActivityCompletionAlert();
          return;
        }
        setIsActivityOver(true);
      }
    },
    [state]
  );

  const setShowAnimation = useCallback(
    (showAnimation: boolean) => {
      dispatch({ type: 'SET_SHOW_ANIMATION', payload: { showAnimation } });
    },
    [dispatch]
  );

  useEffect(() => {
    assignmentActivityId &&
      handleSaveAttempt({
        assignmentActivityId: JSON.parse(assignmentActivityId),
        status: AttemptStatus.IN_PROGRESS as any,
        dateStarted: new Date(),
      });
  }, [assignmentActivityId]);

  useEffect(() => {
    const getCurrentData = state.activityData.pages
      ? getCurrentPageData(
          state.activityData.pages,
          false,
          (page, pageNumber) => page.page.pageNumber === pageNumber
        )
      : [];
    setCurrentData(getCurrentData);
  }, [state.activityData, currentPage, noOfPages]);

  return (
    <GameContext.Provider
      value={[
        state,
        {
          onGameEnd,
          resetActivity,
          handleCurrentPageCompletion,
          handleCurrentPageCompletionWithAlert,
          handleRestartActivity,
          triggerActivityModal,
          handleTurnActivityPage,
          handleCloseActivity,
          setShowAnimation,
        },
      ]}
    >
      {props.children}
      {state.isActivityOver && (
        <NextButton onClick={handleActivityCompletion} size={ButtonSize.SMALL} pulsing />
      )}
    </GameContext.Provider>
  );
};

export default GameProvider;
